import * as React from 'react';
import { useAtomValue } from 'jotai';
import { useCommonInfoStore } from '@shared/store';
import * as store from './store';
import { TIMEOUT_COEFFICIENTS } from '@src/shared/constants/analysis';

export const usePatternQueryPayload = () => {
  const interval = store.useDashboardInterval();
  const filters = store.useDashboardFilters();
  const pattern = store.useDashboardPattern();
  const durationLimit = store.useDashboardDurationLimit();
  const propagations = store.useDashboardPropagationSettings();

  const basePayload = React.useMemo(
    () => ({
      time_range: interval,
      filters: (filters ?? []).map((group) =>
        group.map((item) => ({
          ...item,
          type: propagations[item.field] ?? 'context',
        })),
      ) as FilterValueWithPropagation,
      pattern,
      timeout_ms: durationLimit.val * TIMEOUT_COEFFICIENTS[durationLimit.unit],
      limit: 200,
    }),
    [durationLimit, filters, interval, pattern, propagations],
  );

  return [
    basePayload,
    {
      interval,
      filters,
      pattern,
      durationLimit,
      propagations,
    },
  ] as const;
};

export const generateShareLink = () => {
  const customerId = useCommonInfoStore.getState().userInfo.customer_id;

  if (!customerId) return;

  const { origin, pathname, search, hash } = window.location;

  return `${origin}/customer_switch/${customerId}/?next=${pathname}${search}${hash}`;
};

export const checkPropagationStepDropoffAvailable = (
  dropoffIndex: number,
  filter: FilterValueWithPropagation = [],
) => {
  const maxStep = Math.max(
    ...filter
      .flat()
      .filter((item) => /step_(\d+)/.test(item.type))
      .map((item) => parseInt(item.type.replace(/step_/, ''), 10)),
  );

  return dropoffIndex >= maxStep;
};

export const useDropoffStatus = (fetching: boolean) => {
  const [basePayload] = usePatternQueryPayload();
  const dropoffIndex = useAtomValue(store.dropOffIndexAtom);

  if (fetching) return 'FETCHING';

  if (basePayload.pattern.length < 2) return 'INSUFFICIENT_STEPS';

  if (!checkPropagationStepDropoffAvailable(dropoffIndex, basePayload.filters))
    return 'INVALID_STEP_PROPAGATION';

  return 'AVAILABLE';
};
